"use client";

import { cn } from "@/src/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import type { VerdictHistoryEntry } from "./types";
import { STATUS_COLOR, StatusIcon, statusText, useVerdictLabels } from "./meta";
import { CommentText } from "./CommentText";

/** Шапка записи: дата · время · ФИО · иконка · цветной текст вердикта */
function EntryHeader({ entry }: { entry: VerdictHistoryEntry }) {
  const labels = useVerdictLabels();
  const isMobile = useIsMobile();
  const status = entry.status;

  // Мобилка: activity-row — ведущая цветная иконка-маркер, действие акцентом,
  // автор/время приглушённо и мельче (чёткая иерархия вместо плоской строки).
  if (isMobile) {
    return (
      <div className="flex items-start gap-2.5">
        <span
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: STATUS_COLOR[status] + "1A" }}
        >
          <StatusIcon status={status} className="size-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm font-semibold" style={{ color: STATUS_COLOR[status] }}>
            {statusText(status, entry.isStatusChange, labels)}
          </p>
          <p className="truncate text-xs text-gray-500">{entry.author}</p>
          <p className="text-xs tabular-nums text-gray-400">
            {entry.date} · {entry.time}
          </p>
        </div>
      </div>
    );
  }

  // Десктоп: компактный один ряд.
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="text-gray-400 tabular-nums">{entry.date}</span>
      <span className="text-gray-400 tabular-nums">{entry.time}</span>
      <span className="text-gray-400">{entry.author}</span>
      <StatusIcon status={status} className="size-4" />
      <span className="font-medium" style={{ color: STATUS_COLOR[status] }}>
        {statusText(status, entry.isStatusChange, labels)}
      </span>
    </div>
  );
}

/** Компактная строка для карточки (превью, без текста комментария) */
export function CompactHistoryRow({ entry, isLast }: { entry: VerdictHistoryEntry; isLast: boolean }) {
  return (
    <div className={cn("w-full py-3 px-1", !isLast && "border-b border-gray-100")}>
      <EntryHeader entry={entry} />
    </div>
  );
}

/** Полная строка для модалок (с текстом комментария, чередующийся фон) */
export function FullHistoryRow({ entry, striped }: { entry: VerdictHistoryEntry; striped: boolean }) {
  return (
    <div className={cn("px-3 py-3 space-y-1.5 w-full", striped ? "bg-gray-50" : "bg-white")}>
      <EntryHeader entry={entry} />
      {entry.comment && <CommentText value={entry.comment} />}
    </div>
  );
}

/**
 * Узел вертикального таймлайна (мобилка): рейка с цветным маркером-иконкой и
 * соединительной линией + контент (действие, автор·время, комментарий).
 * Линия не продолжается у последнего узла (isLast).
 */
export function TimelineRow({ entry, isLast }: { entry: VerdictHistoryEntry; isLast: boolean }) {
  const labels = useVerdictLabels();
  const status = entry.status;
  return (
    <li className="flex gap-3">
      {/* рейка: узел + соединительная линия */}
      <div className="flex flex-col items-center">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full ring-2 ring-white"
          style={{ backgroundColor: STATUS_COLOR[status] + "1A" }}
        >
          <StatusIcon status={status} className="size-4" />
        </span>
        {!isLast && <span className="mt-1 w-px flex-1 bg-gray-200" />}
      </div>

      {/* контент */}
      <div className={cn("min-w-0 flex-1 space-y-1", isLast ? "pb-0" : "pb-6")}>
        <p className="text-sm font-semibold" style={{ color: STATUS_COLOR[status] }}>
          {statusText(status, entry.isStatusChange, labels)}
        </p>
        <p className="truncate text-xs text-gray-500">{entry.author}</p>
        <p className="text-xs tabular-nums text-gray-400">
          {entry.date} · {entry.time}
        </p>
        {entry.comment && (
          <div className="pt-0.5 text-sm">
            <CommentText value={entry.comment} />
          </div>
        )}
      </div>
    </li>
  );
}
